import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCheck } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../../shared/api/client";
import {
  Button,
  Card,
  cn,
  EmptyState,
  ErrorState,
  LoadingState,
  PageHeader,
} from "../../shared/ui";
import type {
  NotificationCategory,
  NotificationView,
  UnreadCount,
} from "../../shared/types/api";
import { NotificationRow } from "./NotificationRow";
import {
  CATEGORY_LABELS,
  CATEGORY_ORDER,
  filterNotifications,
  groupByDay,
} from "./notificationVocabulary";

/** One request's worth of history. Enough that most people never press for more. */
const PAGE_SIZE = 50;

/**
 * Everything somebody has been told, kept (§89).
 *
 * <p>The bell is a glance at the recent end. This is the record, and it exists because a
 * notification centre is only worth having if not looking at it costs nothing: somebody back from
 * two weeks away should be able to read what happened while they were gone, in order, rather than
 * discover that the beginning of it scrolled off a fixed-size list.
 *
 * <p>Days are the unit, because a day is what people remember something happening in. Within a
 * day the order is the order it happened in, newest first.
 */
export function NotificationsPage() {
  const [category, setCategory] = useState<NotificationCategory | null>(null);
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [pages, setPages] = useState(1);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const notifications = useQuery({
    queryKey: ["notifications", "history", pages],
    queryFn: async () => {
      const requests = Array.from({ length: pages }, (_, page) =>
        api.get<NotificationView[]>(
          `/api/v1/my/notifications?page=${page}&size=${PAGE_SIZE}`,
        ),
      );
      return (await Promise.all(requests)).flat();
    },
  });

  const unread = useQuery({
    queryKey: ["notifications", "unread"],
    queryFn: () => api.get<UnreadCount>("/api/v1/my/notifications/unread-count"),
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ["notifications"] });
  };

  const markAll = useMutation({
    mutationFn: () => api.post<void>("/api/v1/my/notifications/read-all"),
    onSuccess: refresh,
  });

  const openNotification = async (notification: NotificationView) => {
    if (!notification.read) {
      await api.post(`/api/v1/my/notifications/${notification.id}/read`);
      refresh();
    }
    if (notification.link) {
      navigate(notification.link);
    }
  };

  const all = notifications.data ?? [];
  const shown = filterNotifications(all, { category, unreadOnly });
  const groups = groupByDay(shown);
  const unreadCount = unread.data?.unread ?? 0;

  // Only the bands somebody actually has notifications in. A tab that leads to an empty list is
  // a click spent finding out there was nothing there.
  const bands = CATEGORY_ORDER.filter((band) => all.some((each) => each.category === band));

  return (
    <div className="mx-auto max-w-3xl">
      <PageHeader
        title="Notifications"
        subtitle="Everything you have been told, kept in full. Nothing here disappears because you did not open it."
        actions={
          unreadCount > 0 && (
            <Button
              variant="secondary"
              onClick={() => markAll.mutate()}
              disabled={markAll.isPending}
            >
              <CheckCheck className="h-4 w-4" />
              Mark all read
            </Button>
          )
        }
      />

      <div className="mb-4 flex flex-wrap items-center gap-1.5">
        <FilterChip active={category === null} onClick={() => setCategory(null)}>
          Everything
        </FilterChip>
        {bands.map((band) => (
          <FilterChip
            key={band}
            active={category === band}
            onClick={() => setCategory(band)}
          >
            {CATEGORY_LABELS[band]}
          </FilterChip>
        ))}
        <span className="mx-1 h-4 w-px bg-ink-200" aria-hidden />
        <FilterChip active={unreadOnly} onClick={() => setUnreadOnly((was) => !was)}>
          Unread{unreadCount > 0 ? ` (${unreadCount})` : ""}
        </FilterChip>
      </div>

      {notifications.isPending ? (
        <LoadingState />
      ) : notifications.isError ? (
        <ErrorState error={notifications.error} onRetry={refresh} />
      ) : shown.length === 0 ? (
        <Card>
          <EmptyState
            title={all.length === 0 ? "Nothing yet" : "Nothing under this filter"}
            message={
              all.length === 0
                ? "You will hear about work assigned to you, mentions, blockers and anything waiting on your decision."
                : "Everything you have been told is still here — widen the filter to see it."
            }
          />
        </Card>
      ) : (
        <div className="space-y-6">
          {groups.map((group) => (
            <section key={group.label}>
              <h2 className="mb-2 text-xs font-medium text-ink-500">{group.label}</h2>
              <Card className="overflow-hidden">
                <ul className="divide-y divide-ink-100">
                  {group.items.map((notification) => (
                    <li key={notification.id}>
                      <NotificationRow
                        notification={notification}
                        onOpen={(each) => void openNotification(each)}
                      />
                    </li>
                  ))}
                </ul>
              </Card>
            </section>
          ))}

          {all.length >= pages * PAGE_SIZE && (
            <div className="text-center">
              <Button variant="secondary" onClick={() => setPages((was) => was + 1)}>
                Show older
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/** A filter is a state somebody is in, so the chip shows which one they are in. */
function FilterChip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        "rounded-full px-3 py-1 text-xs font-medium transition-colors",
        active
          ? "bg-brand-600 text-white"
          : "bg-ink-100 text-ink-600 hover:bg-ink-200 hover:text-ink-900",
      )}
    >
      {children}
    </button>
  );
}
